<!--
  TadbeesaPage — /tadbeesa/ — «تدبيسة النادي», the club's own shelf.

  Every piece listed here is already at the club, so it ships with her carton
  for certain. The page lists ONLY listings with a piece still on the shelf —
  `filter[resaleAvailable]=1` (BE→FE brief 2026-09-23-tadbeesa-shelf-filter) —
  newest first, using the same PostCard as the feed, under a two-line intro
  and the channel's PoolShare chip. Before this page, seeing the whole shelf
  took the filter sheet and three taps, and it came back with every sold-out
  listing of the past year mixed in.

  ISOLATED TABLE (the one trap on this page): the cards read `TadbeesaPost`,
  never `Post`. usePostsFeed flushes its model's table on every reset, and the
  home feed renders from `Post` — including the moment she comes back: with
  preloadPreviousPage / iosSwipeBack off (App.vue), F7 removes the previous
  page after every push, so home is rebuilt on back and paints its `Post` rows
  before its own refetch lands. A shared table would hand her back the shelf's
  rows, or nothing. The table is also flushed when this page unmounts, so a
  later visit — or another surface looking a post up — never reads a previous
  visit's rows. See models/posts/TadbeesaPost.js.

  ONE LIST FOR THE PAGE'S LIFE: <virtual-post-list> renders the page's
  <f7-page-content ptr>. Framework7 creates pull-to-refresh only at pageInit
  (framework7/components/pull-to-refresh/pull-to-refresh.js pageInit), so a
  page-content swapped in later would never get it. The list is mounted from
  the first render and driven by props, like HomePage's.

  THE CHANNEL ID ARRIVES LATE: it comes from useTadbeesaChannel (cached per
  user, but a first visit waits on a request). It rides in `filters.channels`,
  and the feed's endpoint is null until it is there, so no request ever goes
  out without it. Until the first page for that id has landed, the list shows
  the loader — never the empty state — so a shelf that has pieces never
  flashes "empty" first.

  ROUTE INVARIANTS (playbook Section 5, mirrors PostDetailPage):
   - EAGER `component:` in routes.js — a reload can make this the boot URL, and
     `asyncComponent:` would paint "[object Promise]" (row 16).
   - requireMembership; the cold hard-load guard skip (row 22) is covered by
     App.vue's EAGER_MEMBER_REQUIRED_PREFIXES.
   - `meta.hideTabbar: true`; slashless normalizer AFTER the strict route (row 20).
-->
<template>
  <f7-page
    name="tadbeesa"
    :page-content="false"
    class="tadbeesa-page"
    @page:beforein="onPageBeforeIn"
  >
    <f7-navbar class="tadbeesa-navbar" :title="t('tadbeesa.name')">
      <template #left>
        <f7-link class="tadbeesa-page__back" @click="leave">
          <f7-icon f7="chevron_right" size="22"></f7-icon>
          <span class="tadbeesa-page__back-label">{{ t('general.back') }}</span>
        </f7-link>
      </template>
    </f7-navbar>

    <!-- navbar-selector: VirtualPostList hides/shows THIS page's navbar while
         scrolling — the default ('.home-navbar') would reach the home tab's
         navbar sitting underneath this pushed page. -->
    <virtual-post-list
      ref="virtualListRef"
      :items="shelfItems"
      :loading="awaitingShelf || loading"
      :error="listError"
      :is-loading-more="isLoadingMore"
      :has-more="hasMore"
      current-feed="tadbeesa"
      navbar-selector=".tadbeesa-navbar"
      :estimated-item-size="650"
      :overscan="2"
      @load-more="loadMore"
      @refresh="handlePullToRefresh"
      @retry="onRetry"
    >
      <template #header>
        <!-- The intro speaks about the pieces below it, so it goes with them:
             hidden while loading, on an error, and when the shelf is empty. -->
        <section v-if="shelfItems.length > 0 && !listError" class="tadbeesa-intro">
          <p class="tadbeesa-intro__certainty">
            <f7-icon f7="checkmark_circle" size="20" class="tadbeesa-intro__check"></f7-icon>
            <span>{{ t('tadbeesa.intro.certainty') }}</span>
          </p>
          <p class="tadbeesa-intro__terms">{{ t('tadbeesa.intro.terms') }}</p>
          <div v-if="poolShareRate" class="tadbeesa-intro__chip">
            <pool-share-rate-chip :rate="poolShareRate"></pool-share-rate-chip>
          </div>
        </section>
      </template>

      <!-- The header tap does nothing here: she is already on the shelf. -->
      <template #item="{ item }">
        <post-card :post-id="item.id" current-feed="tadbeesa"></post-card>
      </template>

      <template #empty>
        <div class="tadbeesa-empty">
          <div class="tadbeesa-empty__icon">
            <f7-icon f7="cube_box" size="36"></f7-icon>
          </div>
          <p class="tadbeesa-empty__title">{{ t('tadbeesa.empty.title') }}</p>
          <p class="tadbeesa-empty__body">{{ t('tadbeesa.empty.body') }}</p>
          <f7-button outline round class="tadbeesa-empty__cta" @click="goHome">
            {{ t('tadbeesa.empty.cta') }}
          </f7-button>
        </div>
      </template>
    </virtual-post-list>

    <!-- Every «تعرف مين عنده؟» door on a card needs a host — one sheet at page
         level, outside the transformed virtual rows (see HomePage). -->
    <product-suggestion-host></product-suggestion-host>
  </f7-page>
</template>

<script setup>
import { useRepo } from 'pinia-orm';
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import PoolShareRateChip from '@/components/posts/PoolShareRateChip.vue';
import PostCard from '@/components/posts/PostCard.vue';
import ProductSuggestionHost from '@/components/posts/ProductSuggestionHost.vue';
import VirtualPostList from '@/components/posts/VirtualPostList.vue';
import { usePostsFeed } from '@/composables/usePostsFeed.js';
import { useTadbeesaChannel } from '@/composables/useTadbeesaChannel.js';
import TadbeesaPost from '@/models/posts/TadbeesaPost.js';

// F7-Vue passes f7route / f7router as props to `component:` routes.
const props = defineProps({
  f7route: { type: Object, default: null },
  f7router: { type: Object, default: null },
});

const { t } = useI18n({ useScope: 'global' });
const virtualListRef = ref(null);

const { channelId, poolShareRate, ready, failed, retry } = useTadbeesaChannel();

const {
  items,
  filters,
  loading,
  isLoadingMore,
  isRefreshing,
  error,
  hasMore,
  loadInitial,
  loadMore,
  refresh,
} = usePostsFeed({
  initialFeed: 'tadbeesa',
  // The feed's own include set (HomePage, PostDetailPage, useCart.fetchPostData)
  // — `products.variants` carries each size's own piece count for the cart.
  include: 'mediables,media,channel,products,categories,products.variants',
  withCount: 'bookmarks',
  pageSize: 10,
  onAfterRefresh: () => virtualListRef.value?.scrollToTop?.(false),
  feeds: {
    tadbeesa: {
      model: TadbeesaPost,
      // Cursor mode, no sort: newest first is the recency cursor itself.
      cursorOnly: false,
      // No channel id yet → no request (a bare `filter[resaleAvailable]`
      // would still work today, but only because no other channel lists
      // pieces — the channel filter is what makes this THE club's shelf).
      endpoint: (activeFilters) => (activeFilters.channels?.length ? '/posts' : null),
      staticFilters: { resaleAvailable: 1 },
      orderBy: (repo) => repo.orderBy('publishedAt', 'desc').get(),
    },
  },
});

// ---- Channel id → first page ----------------------------------------------
//
// The feed loads once on mount (usePostsFeed's onMounted); on a first visit
// the channel id is not known yet, so that load finds a null endpoint and
// only clears this page's table. The id is then handed to the feed exactly
// once, as soon as the engine is not mid-refresh: loadInitial() quietly
// returns while `isRefreshing` is set (it stays set ~350ms after every load),
// so an id that lands inside that window would otherwise never load. Watching
// `isRefreshing` too means it waits the window out instead of being dropped.
// When the id is already cached this runs during setup, and the mount-time
// load is the one that returns early.
let requestedChannelId = null;
const landedChannelId = ref(null);

async function loadShelf(id) {
  requestedChannelId = id;
  filters.value.channels = [id];
  try {
    await loadInitial();
  } finally {
    // Landed either way: a failed first page shows the error block (with its
    // retry), never an endless loader.
    landedChannelId.value = id;
  }
}

watch(
  [channelId, isRefreshing],
  ([id, refreshing]) => {
    if (id == null || refreshing || requestedChannelId === id) return;
    loadShelf(id);
  },
  { immediate: true },
);

// True until the shelf's first page for the current channel id has landed.
// A lookup that resolved to no channel is settled, not pending: the page
// shows its empty state (no such channel) or its error block (the lookup
// failed) rather than a loader that never ends.
const awaitingShelf = computed(
  () => !ready.value || (channelId.value != null && landedChannelId.value !== channelId.value),
);

// Nothing from an earlier load is shown until the fresh first page has
// replaced it.
const shelfItems = computed(() => (awaitingShelf.value ? [] : items.value));

// The channel lookup failing is NOT an empty shelf — saying «فاضية» would be
// a false fact. It gets the list's error block, whose retry asks again.
const lookupFailed = computed(() => ready.value && failed.value);
// One member-facing sentence for both failures; the feed's own `error` is a
// raw transport message (e.g. "Network Error").
const listError = computed(() => {
  if (error.value || lookupFailed.value) return t('errors.loadFailed');
  return null;
});

function onRetry() {
  if (lookupFailed.value) retry();
  else loadInitial();
}

async function handlePullToRefresh(done) {
  if (isRefreshing.value) {
    if (typeof done === 'function') done();
    return;
  }
  try {
    if (lookupFailed.value) await retry();
    else await refresh();
  } finally {
    if (typeof done === 'function') done();
  }
}

onBeforeUnmount(() => {
  useRepo(TadbeesaPost).flush();
});

// ---- Leaving ----------------------------------------------------------------
//
// router.back() is only safe after an in-app push. On a reload of /tadbeesa/,
// F7's browserHistory boot FABRICATES a history of ['/', '/tadbeesa/']
// (framework7/modules/router/router-class.js getInitialUrl) and back() then
// calls the browser's History.back() — which leaves the app (about:blank in a
// fresh tab). F7 reports how the page arrived: `from: 'next'` only for a
// forward push (navigate.js); a boot or reload arrives as 'current'.
let pushedInApp = false;
function onPageBeforeIn(page) {
  pushedInApp = page?.from === 'next';
}

/** The empty state's way out: the home feed. Back when home is right behind
 *  this page (a real browser-history entry to pop), otherwise straight to it.
 *  Home is rebuilt either way — F7 removed it when this page was pushed. */
function goHome() {
  const r = props.f7router;
  const previous = r?.history?.[r.history.length - 2];
  if (pushedInApp && previous?.split('?')[0] === '/') r.back();
  else r?.navigate('/', { clearPreviousHistory: true });
}

/** Back where she came from; the home feed when she arrived by a reload. */
function leave() {
  const r = props.f7router;
  if (pushedInApp && r?.history?.length > 1) r.back();
  else goHome();
}
</script>

<style scoped>
.tadbeesa-page__back {
  display: inline-flex;
  align-items: center;
  gap: 2px;
}

.tadbeesa-page__back-label {
  font-size: 15px;
}

/* The intro — one warm card above the list (canvas board 2). Measured
   contrast: #1f2937 on #fffbeb 14.2:1, #4b5563 on #fffbeb 7.29:1. */
.tadbeesa-intro {
  display: flex;
  flex-direction: column;
  gap: 6px;
  /* No bottom margin: the list keeps its own 12px gap above the first card. */
  margin: 12px 16px 0;
  padding: 14px;
  border-radius: 16px;
  background: var(--poolit-warning-50, #fffbeb);
  border: 1px solid #fde68a;
}

.tadbeesa-intro__certainty {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.45;
  color: #1f2937;
}

.tadbeesa-intro__check {
  flex-shrink: 0;
  color: var(--poolit-success-700, #047857);
}

.tadbeesa-intro__terms {
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: #4b5563;
}

.tadbeesa-intro__chip {
  display: flex;
}

.dark .tadbeesa-intro {
  background: color-mix(in srgb, var(--poolit-warning-500) 12%, transparent);
  border-color: color-mix(in srgb, var(--poolit-warning-500) 35%, transparent);
}

.dark .tadbeesa-intro__certainty {
  color: var(--f7-text-color);
}

.dark .tadbeesa-intro__terms {
  color: color-mix(in srgb, var(--f7-text-color) 72%, transparent);
}

.dark .tadbeesa-intro__check {
  color: var(--poolit-success-500, #10b981);
}

/* The empty shelf (canvas board 3). */
.tadbeesa-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 0 12px;
  text-align: center;
}

.tadbeesa-empty__icon {
  display: grid;
  place-items: center;
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: var(--poolit-warning-100, #fef3c7);
  color: var(--poolit-warning-700, #b45309);
}

.tadbeesa-empty__title {
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  color: var(--f7-text-color);
}

.tadbeesa-empty__body {
  margin: 0;
  font-size: 14px;
  line-height: 1.6;
  color: #4b5563;
}

.dark .tadbeesa-empty__body {
  color: color-mix(in srgb, var(--f7-text-color) 72%, transparent);
}

/* The app's #8e5bfe purple is 4.14:1 on white — below AA for 15px text — so
   the outline takes the darker brand foreground (#6621e2 light, #b794ff dark). */
.tadbeesa-empty__cta {
  --f7-button-text-color: var(--ci-brand-fg, #6621e2);
  --f7-button-outline-border-color: var(--ci-brand-fg, #6621e2);
  --f7-button-height: 44px;
  --f7-button-font-size: 15px;
  --f7-button-font-weight: 600;
  margin-top: 8px;
  min-width: 180px;
}
</style>
